import { useEffect, useRef, useState } from "react";
import { api, API, auth } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";
import { Palette, Save, AlertTriangle, Loader2, Trash2, Eye, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PRESETS = ["#0EA5E9", "#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#0F766E"];

export default function Settings() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({
    hospital_name: "", tagline: "", logo_path: "", cover_path: "", theme_color: "#0EA5E9",
    quiz_validation_fields: ["email"],
    // Certificate customization
    cert_template: "classic", cert_logo_path: "", cert_body_text: "",
    cert_signatory_1_name: "", cert_signatory_1_designation: "", cert_signatory_1_signature_path: "",
    cert_signatory_2_name: "", cert_signatory_2_designation: "", cert_signatory_2_signature_path: "",
    cert_show_verify_qr: true,
  });
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetOpts, setResetOpts] = useState({ keep_branding: true, keep_integrations: false, keep_audit_logs: false });
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    api.get("/branding").then(r => setForm({
      hospital_name: r.data.hospital_name || "",
      tagline: r.data.tagline || "",
      logo_path: r.data.logo_path || "",
      cover_path: r.data.cover_path || "",
      theme_color: r.data.theme_color || "#0EA5E9",
      quiz_validation_fields: r.data.quiz_validation_fields || ["email"],
      cert_template: r.data.cert_template || "classic",
      cert_logo_path: r.data.cert_logo_path || "",
      cert_body_text: r.data.cert_body_text || "",
      cert_signatory_1_name: r.data.cert_signatory_1_name || "",
      cert_signatory_1_designation: r.data.cert_signatory_1_designation || "",
      cert_signatory_1_signature_path: r.data.cert_signatory_1_signature_path || "",
      cert_signatory_2_name: r.data.cert_signatory_2_name || "",
      cert_signatory_2_designation: r.data.cert_signatory_2_designation || "",
      cert_signatory_2_signature_path: r.data.cert_signatory_2_signature_path || "",
      cert_show_verify_qr: r.data.cert_show_verify_qr !== false,
    }));
  }, []);

  function toggleValidationField(key) {
    setForm((f) => {
      const cur = new Set(f.quiz_validation_fields || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...f, quiz_validation_fields: Array.from(cur) };
    });
  }

  async function save() {
    try {
      await api.put("/branding", form);
      await refresh();
      toast.success("Branding updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-4xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: form.theme_color }}>Customize</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Hospital Branding</h1>
        <p className="text-slate-500 mt-1">Your logo, name, and theme color show up on the login page, dashboard, employee portal, and quiz player.</p>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-6 grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Hospital Name</Label>
              <Input value={form.hospital_name} onChange={e=>setForm({...form, hospital_name: e.target.value})} data-testid="brand-name"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Tagline</Label>
              <Input value={form.tagline} onChange={e=>setForm({...form, tagline: e.target.value})} data-testid="brand-tagline"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5"/>Theme Color</Label>
              <div className="flex gap-2 flex-wrap mt-1.5">
                {PRESETS.map(c => (
                  <button key={c} onClick={()=>setForm({...form, theme_color: c})}
                    className={`h-9 w-9 rounded-xl transition-transform ${form.theme_color === c ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : ""}`}
                    style={{ background: c }} data-testid={`color-${c}`} aria-label={c}/>
                ))}
                <label className="h-9 w-9 rounded-xl border-2 border-slate-200 cursor-pointer overflow-hidden grid place-items-center">
                  <input type="color" value={form.theme_color} onChange={e=>setForm({...form, theme_color: e.target.value})}
                    className="opacity-0 absolute h-0 w-0"/>
                  <span className="text-[10px] text-slate-500">Custom</span>
                </label>
              </div>
              <div className="text-xs text-slate-500 mt-1.5">Current: <span className="font-mono font-bold" style={{ color: form.theme_color }}>{form.theme_color}</span></div>
            </div>
            <Button onClick={save} className="text-white" style={{ background: form.theme_color }} data-testid="brand-save"><Save className="h-4 w-4 mr-1.5"/>Save Branding</Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Hospital Logo</Label>
              <div className="mt-1.5 max-w-[200px]">
                <ImageUpload value={form.logo_path} onChange={(p)=>setForm({...form, logo_path: p})} aspect="aspect-square" testid="logo"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Hero / Cover Image</Label>
              <ImageUpload value={form.cover_path} onChange={(p)=>setForm({...form, cover_path: p})} testid="cover"/>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-2xl" data-testid="quiz-validation-card">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: form.theme_color }}>Quiz Entry — Default Validation</div>
            <h2 className="font-display font-black text-xl text-slate-900 mt-0.5">What must employees verify to start a training?</h2>
            <p className="text-slate-500 mt-1 text-sm">
              <span className="font-mono font-bold text-slate-700">Employee ID</span> is always required.
              Toggle the additional checks below — applies as the global default. Individual quizzes can override this in the quiz editor.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ValToggle
              checked={form.quiz_validation_fields?.includes("email")}
              onChange={() => toggleValidationField("email")}
              testid="val-email"
              label="Email"
              hint="Verify the registered email matches the Employee ID."
            />
            <ValToggle
              checked={form.quiz_validation_fields?.includes("dob")}
              onChange={() => toggleValidationField("dob")}
              testid="val-dob"
              label="Date of Birth (DD/MM/YYYY)"
              hint="Verify DOB matches employee master. Requires DOB stored for each employee."
            />
          </div>
          <Button onClick={save} className="text-white" style={{ background: form.theme_color }} data-testid="val-save"><Save className="h-4 w-4 mr-1.5"/>Save Validation Settings</Button>
        </CardContent>
      </Card>

      {/* Certificate customization */}
      <Card className="border-slate-200 rounded-2xl" data-testid="cert-card">
        <CardContent className="p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: form.theme_color }}>Completion Certificate</div>
            <h2 className="font-display font-black text-xl text-slate-900 mt-0.5">Design & signatories</h2>
            <p className="text-slate-500 mt-1 text-sm">These settings apply to every certificate PDF issued after a completed quiz.</p>
          </div>

          {/* Template picker */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Template</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { v: "classic",  label: "Classic",  hint: "Bold serif title + brand divider" },
                { v: "modern",   label: "Modern",   hint: "Clean sans + accent bar" },
                { v: "elegant",  label: "Elegant",  hint: "Italic gold styling for premium look" },
              ].map(t => (
                <button key={t.v} type="button" onClick={() => setForm({ ...form, cert_template: t.v })}
                  data-testid={`cert-tpl-${t.v}`}
                  className={`text-left rounded-2xl border-2 p-4 transition ${form.cert_template === t.v ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-bold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Logo override + body text */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Certificate Logo (falls back to hospital logo)</Label>
              <div className="mt-1.5 max-w-[200px]">
                <ImageUpload value={form.cert_logo_path} onChange={(p) => setForm({ ...form, cert_logo_path: p })} aspect="aspect-square" testid="cert-logo"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Body Text</Label>
              <textarea value={form.cert_body_text} onChange={e => setForm({ ...form, cert_body_text: e.target.value })}
                rows={4} className="w-full mt-1.5 text-sm border border-slate-200 rounded-xl p-3 font-mono"
                placeholder="has successfully completed the training {quiz} with a score of {score} on {date}."
                data-testid="cert-body-text"/>
              <div className="text-[11px] text-slate-500 mt-1">Placeholders: <code>{"{name}"}</code> <code>{"{quiz}"}</code> <code>{"{score}"}</code> <code>{"{date}"}</code> <code>{"{emp_id}"}</code> <code>{"{designation}"}</code></div>
            </div>
          </div>

          {/* Signatories + live preview split */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <SigBlock idx={1} form={form} setForm={setForm}/>
                <SigBlock idx={2} form={form} setForm={setForm}/>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="cert-qr-toggle">
                <input type="checkbox" checked={!!form.cert_show_verify_qr}
                  onChange={e => setForm({ ...form, cert_show_verify_qr: e.target.checked })}/>
                <span>Show public verification QR code on the certificate</span>
              </label>
            </div>
            <CertificatePreview form={form}/>
          </div>

          <Button onClick={save} className="text-white" style={{ background: form.theme_color }} data-testid="cert-save">
            <Save className="h-4 w-4 mr-1.5"/>Save Certificate Settings
          </Button>
        </CardContent>
      </Card>

      <Card className="border-red-200 rounded-2xl bg-red-50/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-red-100 text-red-600 grid place-items-center"><AlertTriangle className="h-5 w-5"/></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-red-700">Danger Zone</div>
              <h3 className="font-display font-black text-lg mt-0.5 text-red-900">Factory Reset</h3>
              <p className="text-sm text-red-900/70 mt-1">Permanently delete all employees, masters, quizzes, assignments and attempts. Your admin login and branding stay intact. Use this only when starting a fresh data load.</p>
            </div>
            <Button variant="outline" onClick={()=>{ setResetOpen(true); setConfirmText(""); }}
              data-testid="open-factory-reset"
              className="border-red-300 text-red-700 hover:bg-red-100">
              <Trash2 className="h-4 w-4 mr-1.5"/>Factory Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5"/>Confirm Factory Reset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-900">
              <div className="font-bold">This will permanently delete:</div>
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                <li>All Employees</li>
                <li>All Masters (Departments, Sub-Departments, Categories, Locations)</li>
                <li>All Quizzes & Questions</li>
                <li>All Assignments & Attempts (scores)</li>
                <li>Uploaded files metadata + queued jobs</li>
                <li>Non-admin user accounts (employee/trainer/manager logins)</li>
              </ul>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2"><input type="checkbox" checked={resetOpts.keep_branding} onChange={e=>setResetOpts({...resetOpts, keep_branding: e.target.checked})}/> Keep hospital branding</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={resetOpts.keep_integrations} onChange={e=>setResetOpts({...resetOpts, keep_integrations: e.target.checked})}/> Keep integration credentials</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={resetOpts.keep_audit_logs} onChange={e=>setResetOpts({...resetOpts, keep_audit_logs: e.target.checked})}/> Keep audit logs (ISO 27001)</label>
            </div>
            <div>
              <Label className="text-xs font-bold text-red-800">Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded">RESET</span> to confirm</Label>
              <Input value={confirmText} onChange={e=>setConfirmText(e.target.value)} className="border-red-300 mt-1" data-testid="confirm-reset-input"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setResetOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              setResetting(true);
              try {
                const r = await api.post("/admin/factory-reset", { confirmation: confirmText, ...resetOpts });
                const total = Object.values(r.data.cleared).reduce((a,b)=>a+b, 0);
                toast.success(`Deleted ${total} records. Fresh start ready.`);
                setResetOpen(false); setConfirmText("");
                if (!resetOpts.keep_branding) await refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Reset failed");
              } finally { setResetting(false); }
            }} disabled={resetting || confirmText !== "RESET"}
              data-testid="confirm-reset-btn"
              className="bg-red-600 hover:bg-red-700">
              {resetting ? <Loader2 className="h-4 w-4 animate-spin"/> : "Delete Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ValToggle({ checked, onChange, testid, label, hint }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${checked ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={onChange} className="mt-1 h-4 w-4 accent-sky-600"/>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      </div>
    </label>
  );
}

function SigBlock({ idx, form, setForm }) {
  const k = (suffix) => `cert_signatory_${idx}_${suffix}`;
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-2" data-testid={`sig-block-${idx}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
        Signatory {idx} {idx === 2 && <span className="text-slate-400 lowercase font-normal">(optional)</span>}
      </div>
      <div>
        <Label className="text-xs text-slate-500">Name</Label>
        <Input value={form[k("name")]} onChange={e => setForm({ ...form, [k("name")]: e.target.value })}
          placeholder={idx === 1 ? "e.g. Dr. Ramesh Kumar" : "Optional second signatory"}
          data-testid={`sig-${idx}-name`}/>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Designation</Label>
        <Input value={form[k("designation")]} onChange={e => setForm({ ...form, [k("designation")]: e.target.value })}
          placeholder={idx === 1 ? "Medical Superintendent" : "Head of Training"}
          data-testid={`sig-${idx}-designation`}/>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Signature Image</Label>
        <ImageUpload value={form[k("signature_path")]} onChange={(p) => setForm({ ...form, [k("signature_path")]: p })}
          aspect="aspect-[3/1]" testid={`sig-${idx}-img`}/>
        <div className="text-[11px] text-slate-500 mt-1">PNG with transparent background prints best on the PDF.</div>
      </div>
    </div>
  );
}


/** Live-updating sample certificate preview. Debounces form changes to avoid
 * hammering the backend as admins type.
 */
function CertificatePreview({ form }) {
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const lastUrlRef = useRef("");

  // Fields relevant to the preview — used as debounce trigger via JSON stringify.
  const key = JSON.stringify({
    theme_color: form.theme_color,
    hospital_name: form.hospital_name,
    tagline: form.tagline,
    logo_path: form.logo_path,
    cert_template: form.cert_template,
    cert_logo_path: form.cert_logo_path,
    cert_body_text: form.cert_body_text,
    s1n: form.cert_signatory_1_name,
    s1d: form.cert_signatory_1_designation,
    s1s: form.cert_signatory_1_signature_path,
    s2n: form.cert_signatory_2_name,
    s2d: form.cert_signatory_2_designation,
    s2s: form.cert_signatory_2_signature_path,
    qr: form.cert_show_verify_qr,
  });

  async function build() {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const resp = await fetch(`${API}/certificate/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.token()}` },
        body: JSON.stringify({
          hospital_name: form.hospital_name || null,
          tagline: form.tagline || null,
          theme_color: form.theme_color || null,
          logo_path: form.logo_path || null,
          cert_template: form.cert_template || null,
          cert_logo_path: form.cert_logo_path || null,
          cert_body_text: form.cert_body_text || null,
          cert_signatory_1_name: form.cert_signatory_1_name || null,
          cert_signatory_1_designation: form.cert_signatory_1_designation || null,
          cert_signatory_1_signature_path: form.cert_signatory_1_signature_path || null,
          cert_signatory_2_name: form.cert_signatory_2_name || null,
          cert_signatory_2_designation: form.cert_signatory_2_designation || null,
          cert_signatory_2_signature_path: form.cert_signatory_2_signature_path || null,
          cert_show_verify_qr: form.cert_show_verify_qr,
        }),
        signal: ctrl.signal,
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
      lastUrlRef.current = url;
      setPdfUrl(url);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Preview failed");
    } finally {
      if (abortRef.current === ctrl) setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(build, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => () => { if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current); }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3 flex flex-col" data-testid="cert-preview">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold inline-flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5"/>Live Preview
          {loading && <Loader2 className="h-3 w-3 animate-spin text-sky-500 ml-1"/>}
        </div>
        <button type="button" onClick={build} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="cert-preview-refresh">
          <RefreshCw className="h-3 w-3"/>Refresh
        </button>
      </div>
      <div className="flex-1 rounded-xl overflow-hidden bg-white border border-slate-200 min-h-[420px]">
        {pdfUrl ? (
          <iframe src={pdfUrl} title="Certificate preview" className="w-full h-full min-h-[420px]" data-testid="cert-preview-frame"/>
        ) : (
          <div className="h-full min-h-[420px] grid place-items-center text-slate-400 text-sm">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-sky-500"/> : "Preview will appear here"}
          </div>
        )}
      </div>
      <div className="text-[10px] text-slate-500 mt-2 px-1">Sample: Sample Employee · EMP000 · 92% · today's date. Save settings to persist for real certificates.</div>
    </div>
  );
}

